/**
 * MCOS's pharmacy desk (PH-002, PH-003): verify a pharmacy's license before it
 * is activated, record its BAA, and invite its first admin.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, EmptyState, ErrorState, Field, LoadingRows, PageHeader, inputClass } from '../components/ui';

interface Pharmacy {
  id: string;
  name: string;
  kind: string;
  status: 'invited' | 'active' | 'suspended';
  mainPhone: string | null;
  licenseNumber: string | null;
  licenseState: string | null;
  licenseVerifiedOn: string | null;
  baaSignedOn: string | null;
  invitedBy: string | null;
  inviteEmail: string | null;
  contactsReady: { rxIssues: boolean; pricing: boolean };
  users: Array<{ id: string; name: string; email: string; role: string; status: string }>;
  clinics: Array<{ name: string; status: string }>;
  priceLists: Array<{ id: string; name: string }>;
}

const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');
const STATUS = { invited: 'Invited', active: 'Active', suspended: 'Suspended' } as const;

function PharmacyCard({ p }: { p: Pharmacy }) {
  const qc = useQueryClient();
  const [baa, setBaa] = useState(p.baaSignedOn ?? '');
  const [invite, setInvite] = useState({ name: '', email: p.inviteEmail ?? '' });
  const [link, setLink] = useState<string | null>(null);
  const done = (r: { pharmacies: Pharmacy[] }) => qc.setQueryData(['platform-pharmacies'], r);
  const update = useMutation({ mutationFn: (body: Record<string, unknown>) => api.put<{ pharmacies: Pharmacy[] }>(`/platform/pharmacies/${p.id}`, body), onSuccess: done });
  const add = useMutation({
    mutationFn: () => api.post<{ invite: { inviteUrl: string }; pharmacies: Pharmacy[] }>(`/platform/pharmacies/${p.id}/users`, { ...invite, role: 'admin' }),
    onSuccess: (r) => {
      setLink(r.invite.inviteUrl);
      done(r);
    },
  });
  const tone = p.status === 'active' ? 'bg-[#e3efe9] text-forest' : p.status === 'invited' ? 'bg-[#fbf0d6] text-amber-deep' : 'bg-alert-soft text-alert';
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-[20px] text-navy">{p.name}</p>
          <p className="text-[13px] text-[#4d6072]">
            {p.kind}, {p.id}{p.invitedBy ? `, invited by ${p.invitedBy}` : ''}{p.mainPhone ? `, ${p.mainPhone}` : ''}
          </p>
        </div>
        <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${tone}`}>{STATUS[p.status]}</span>
      </div>

      <dl className="mt-4 grid gap-x-6 gap-y-3 text-[14px] sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-[#4d6072]">License</dt>
          <dd className="text-navy">{p.licenseNumber ? `${p.licenseNumber}${p.licenseState ? `, ${p.licenseState}` : ''}` : 'Not given'}</dd>
          <dd className="mt-1">
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" className="accent-forest" checked={p.licenseVerifiedOn != null} onChange={(e) => update.mutate({ licenseVerified: e.target.checked })} />
              {p.licenseVerifiedOn ? `Verified ${p.licenseVerifiedOn}` : 'Mark verified'}
            </label>
          </dd>
        </div>
        <div>
          <dt className="text-[#4d6072]">BAA signed on</dt>
          <dd className="mt-1 flex items-center gap-2">
            <input type="date" className={`${inputClass} py-1.5`} value={baa} onChange={(e) => setBaa(e.target.value)} aria-label={`BAA date for ${p.name}`} />
            <Button size="sm" variant="secondary" disabled={baa === (p.baaSignedOn ?? '')} onClick={() => update.mutate({ baaSignedOn: baa })}>Save</Button>
          </dd>
        </div>
        <div>
          <dt className="text-[#4d6072]">Contacts</dt>
          <dd className="text-navy">Rx issues {p.contactsReady.rxIssues ? 'ready' : 'missing'}, pricing {p.contactsReady.pricing ? 'ready' : 'missing'}</dd>
        </div>
        <div>
          <dt className="text-[#4d6072]">Clinics and lists</dt>
          <dd className="text-navy">{p.clinics.length ? p.clinics.map((c) => `${c.name}${c.status === 'requested' ? ' (asking)' : ''}`).join(', ') : 'No clinics'}</dd>
          <dd className="text-[13px] text-[#4d6072]">{p.priceLists.map((l) => l.name).join(', ') || 'No price lists'}</dd>
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap gap-2">
        {p.status !== 'active' && <Button size="sm" disabled={!p.licenseVerifiedOn} onClick={() => update.mutate({ status: 'active' })} title={p.licenseVerifiedOn ? undefined : 'Verify the license first'}>Activate</Button>}
        {p.status === 'active' && <Button size="sm" variant="secondary" onClick={() => update.mutate({ status: 'suspended' })}>Suspend</Button>}
      </div>
      {update.error && <p className="mt-2 text-sm text-red-600">{errorText(update.error)}</p>}

      <div className="mt-5 border-t border-hairline pt-4">
        <p className="text-[14px] font-semibold text-navy">Users</p>
        {p.users.length ? (
          <ul className="mt-1 text-[14px] text-[#4d6072]">{p.users.map((u) => <li key={u.id}>{u.name}, {u.email}, {u.role}, {u.status.replace('_', ' ')}</li>)}</ul>
        ) : (
          <p className="mt-1 text-[13px] text-[#4d6072]">No one yet. Invite the pharmacy's first admin; they add the rest.</p>
        )}
        <div className="mt-3 grid items-end gap-2 md:grid-cols-[1fr_1fr_auto]">
          <Field label="Name"><input className={inputClass} value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></Field>
          <Field label="Email"><input className={inputClass} type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></Field>
          <Button loading={add.isPending} disabled={!invite.name.trim() || !invite.email.includes('@')} onClick={() => add.mutate()}>Invite admin</Button>
        </div>
        {add.error && <p className="mt-2 text-sm text-red-600">{errorText(add.error)}</p>}
        {link && <p className="mt-2 break-all rounded-md bg-mint px-3 py-2 text-[13px] text-navy" role="status">Send them this link. It works once, for 7 days: {link}</p>}
      </div>
    </Card>
  );
}

export function PlatformPharmaciesPage() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['platform-pharmacies'], queryFn: () => api.get<{ pharmacies: Pharmacy[] }>('/platform/pharmacies') });
  const [form, setForm] = useState({ id: '', name: '', licenseNumber: '', licenseState: '' });
  const create = useMutation({
    mutationFn: () => api.post<{ pharmacies: Pharmacy[] }>('/platform/pharmacies', form),
    onSuccess: (r) => {
      qc.setQueryData(['platform-pharmacies'], r);
      setForm({ id: '', name: '', licenseNumber: '', licenseState: '' });
    },
  });
  return (
    <>
      <PageHeader title="Pharmacies" subtitle="Pharmacies on the portal. Verify the license before activating, and record the BAA: until it is recorded no patient information reaches the pharmacy." />
      {data.error ? (
        <ErrorState error={data.error} retry={() => void data.refetch()} />
      ) : !data.data ? (
        <LoadingRows rows={4} />
      ) : (
        <div className="space-y-5">
          {data.data.pharmacies.length === 0 && <Card><EmptyState title="No pharmacies yet" /></Card>}
          {data.data.pharmacies.map((p) => <PharmacyCard key={p.id} p={p} />)}
          <Card title="Add a pharmacy">
            <div className="grid gap-3 md:grid-cols-4">
              <Field label="Name"><input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, id: form.id || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') })} /></Field>
              <Field label="Id" hint="Lowercase, for example reviverx"><input className={inputClass} value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} /></Field>
              <Field label="License number"><input className={inputClass} value={form.licenseNumber} onChange={(e) => setForm({ ...form, licenseNumber: e.target.value })} /></Field>
              <Field label="License state"><input className={inputClass} maxLength={2} value={form.licenseState} onChange={(e) => setForm({ ...form, licenseState: e.target.value.toUpperCase() })} /></Field>
            </div>
            {create.error && <p className="mt-2 text-sm text-red-600">{errorText(create.error)}</p>}
            <div className="mt-4"><Button loading={create.isPending} disabled={form.name.trim().length < 2 || form.id.length < 2} onClick={() => create.mutate()}>Add pharmacy</Button></div>
          </Card>
        </div>
      )}
    </>
  );
}
